// Line discount on the invoice grids: the operator types either Disc % or
// Disc Amt, and the other one follows. Each line remembers which one was typed
// in `items[i].discountMode` ("pct" or "amt"):
//   - "pct": the amount is worked out from the percentage (the old behaviour)
//   - "amt": the typed amount is kept as it is, and the percentage shown is
//            worked out from it, so a later change of qty or rate keeps the amount
// The saved bill carries both, so the server, print and e-invoice use the amount.

const num = (value) => {
  const n = Number(value);
  return Number.isFinite(n) ? n : 0;
};

/** The discount part of a calcBillTotals line for row `index`. */
export const lineDiscountInput = (watch, index) =>
  watch(`items[${index}].discountMode`) === "amt"
    ? { discountAmount: num(watch(`items[${index}].discountAmount`)) }
    : { discountPercent: watch(`items[${index}].discountPercentage`, 0) };

/**
 * Writes a calculated line back to the form. In "amt" mode the amount the
 * operator is typing is left alone and the percentage follows it.
 */
export const writeLineDiscount = (getValues, setValue, index, line, decimals) => {
  if (getValues(`items[${index}].discountMode`) === "amt") {
    const pct = line.gross ? (line.discount / line.gross) * 100 : 0;
    const shown = Number(pct.toFixed(2)).toString();
    if (String(getValues(`items[${index}].discountPercentage`)) !== shown) {
      setValue(`items[${index}].discountPercentage`, shown);
    }
  } else {
    // only on a change: the field is watched once a line has been in "amt"
    // mode, and an unconditional set here would re-render forever
    const amount = line.discount.toFixed(decimals);
    if (String(getValues(`items[${index}].discountAmount`)) !== amount) {
      setValue(`items[${index}].discountAmount`, amount);
    }
  }
};

/** register() options for the Disc % cell: typing there switches the line to "pct". */
export const discPctEvents = (setValue, index) => ({
  onChange: () => setValue(`items[${index}].discountMode`, "pct"),
});

/** register() options for the Disc Amt cell: typing there switches the line to "amt". */
export const discAmtRules = (setValue, getValues, index) => ({
  onChange: () => setValue(`items[${index}].discountMode`, "amt"),
  validate: (value) => {
    if (value === "" || value === null || value === undefined) return true;
    const amount = Number(value);
    if (isNaN(amount) || amount < 0) return "Discount must be zero or more";
    const gross =
      num(getValues(`items[${index}].quantity`)) * num(getValues(`items[${index}].price`));
    return amount <= gross + 1e-9 || "Discount cannot be more than the line amount";
  },
});

/**
 * The mode for a line loaded from a saved bill: a saved amount is kept as it
 * is, so re-saving an old bill never moves its discount by a rounding step.
 */
export const savedDiscountMode = (discountAmount) => (num(discountAmount) > 0 ? "amt" : "pct");

/** True when any line on the grid carries VAT. */
export const anyLineTaxed = (fields, watch, taxData) =>
  (fields || []).some((_, index) => {
    const taxGUID = watch(`items[${index}].taxPercentage`);
    return num((taxData || []).find((t) => t.guid == taxGUID)?.value) > 0;
  });
